import moment from 'moment-timezone';

import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';

import BaseModel from 'volta/models/base-model';
import { getOrElse } from 'volta/utils/object-utils';
import { sanitized } from 'volta/utils/text-utils';

import type { IDateFilter, TBomSkuFilter } from 'volta/_pods/protected/bom/controller';
import type { IResourceName } from 'volta/models/base-model';
import type Bom from './bom';
import type RouteOperation from './route-operation';
import type SkuModel from './sku';
import type { BomNodeOrOperation } from './types/route-operation';

import { attr, belongsTo, hasMany } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';

const MIN_DATE = '1970-01-01';
const MAX_DATE = '9999-01-01';

export const isActiveAtDate = (
  filter?: IDateFilter,
  startDate?: moment.MomentInput,
  endDate?: moment.MomentInput
) => {
  if (!filter) {
    return true;
  }
  const { startDate: filterStartDate, endDate: filterEndDate } = filter;
  if ((!startDate && !endDate) || (!filterStartDate && !filterEndDate)) {
    return true;
  }

  return !(
    moment.utc(endDate ?? MAX_DATE).isBefore(moment.utc(filterStartDate ?? MIN_DATE)) ||
    moment.utc(startDate ?? MIN_DATE).isAfter(moment.utc(filterEndDate ?? MAX_DATE))
  );
};

export const BomNodeResourceName: IResourceName = {
  singular: 'bomNode',
  plural: 'bomNodes'
};

export default class BomNode extends BaseModel {
  @attr('string') bomId!: string;
  @attr('number') bomLeadTime?: number;
  @attr('number') voltaLeadTime?: number;
  @attr('number') cumulatedLeadTime?: number;
  @attr('number') level!: number;
  @attr('number') qty!: number;
  @attr('number') originalQty!: number;
  @attr('number') fixedQty?: number;
  @attr('number') pcb?: number;
  @attr('string') partUnitMeasure!: string;
  @attr('number') sequence?: number;
  @attr('boolean') onCriticalPath!: boolean;
  @attr('string') parentSkuId?: string;
  @attr('date') startDate?: Date;
  @attr('date') endDate?: Date;
  @attr('number') ltOffset?: number;

  // not async because of https://github.com/emberjs/data/issues/5023 – also see models/bom.ts
  @belongsTo('bom', { async: false, inverse: 'nodes' }) bom!: Bom;
  @belongsTo('sku', { async: false, inverse: null }) sku!: SkuModel;
  @belongsTo('bom-node', { async: false, inverse: 'childNodes' }) parentNode?: BomNode;
  @hasMany('bom-node', { async: false, inverse: 'parentNode' }) childNodes!: BomNode[];
  @hasMany('route-operation', { async: false, inverse: null }) operations!: RouteOperation[];

  @tracked _manualChildren?: BomNodeOrOperation[];

  get skuId() {
    return this.sku.id;
  }

  get partCode() {
    return this.sku.part.code;
  }

  get skuDescription() {
    return this.sku.skuDescription;
  }

  // uses the originalQty if available, qty otherwise
  get displayQty() {
    return getOrElse(this.originalQty, this.qty);
  }

  // Used in BvTable tree mode
  get children(): BomNodeOrOperation[] {
    return this._manualChildren ?? (this.childNodes ? this.childNodes.slice() : []) ?? [];
  }

  set children(childNodes: BomNodeOrOperation[] | undefined) {
    this._manualChildren = childNodes;
  }

  get hasChildren() {
    return this.children.length > 0;
  }

  get formattedLTO() {
    return this.ltOffset
      ? '(' + (this.ltOffset > 0 ? '+' + this.ltOffset : +this.ltOffset) + ')'
      : '';
  }

  get vltDisplay() {
    return this.sku.vltDisplay + this.formattedLTO;
  }

  get hasOperations() {
    return this.operations.length > 0;
  }

  isActiveAtDate(filter?: IDateFilter): boolean {
    return isActiveAtDate(filter, this.startDate, this.endDate);
  }

  isActiveForSkuFilter(skuFilter?: TBomSkuFilter): boolean {
    switch (skuFilter) {
      case 'critical':
        return this.onCriticalPath;
      case 'managed':
        return this.sku.isManaged;
      case 'unprotected':
        return !this.sku.isBuffered && !this.sku.isProtected;
      default:
        return true;
    }
  }

  isNodeActive(dateFilter?: IDateFilter, skuFilter?: TBomSkuFilter, partFilter?: string): boolean {
    return (
      this.isActiveAtDate(dateFilter) &&
      this.isActiveForSkuFilter(skuFilter) &&
      (isEmpty(partFilter) || this.sku.searchName.includes(sanitized(partFilter)))
    );
  }

  [Type] = 'bom-node' as const;
}
